import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  searchPatients,
  getAdmissionStatus,
  getDefaultItemsForVisitType,
  CATEGORIES,
  generateInvoiceNumber,
} from "../billingData";
import { api } from "../../../api/service";
import UPIPaymentPanel from "./UPIPaymentPanel";
import RazorpayModal from "./RazorpayModal";
import hospitalLogo from "../../../assets/swasstiklogo.png";
import "./CreateInvoiceView.css";

const PAYMENT_METHODS = ["Cash", "Online (Razorpay)", "UPI", "Card", "Insurance"];
const BILL_STATUS = { DRAFT: "Draft", AWAITING: "Awaiting Payment", PARTIAL: "Partially Paid", PAID: "Paid", OVERDUE: "Overdue" };

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

const emptyItem = () => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  category: "Consultation",
  itemName: "",
  quantity: 1,
  unitPrice: 0,
  taxPercent: 5,
  itemDiscount: 0,
});

function getBillStatus(grandTotal, totalPaid, dueDate, isFinalized) {
  if (isFinalized) return totalPaid >= grandTotal ? BILL_STATUS.PAID : BILL_STATUS.PARTIAL;
  if (grandTotal <= 0) return BILL_STATUS.DRAFT;
  if (totalPaid >= grandTotal) return BILL_STATUS.PAID;
  if (totalPaid > 0) return BILL_STATUS.PARTIAL;
  if (dueDate) {
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due < today) return BILL_STATUS.OVERDUE;
  }
  return BILL_STATUS.AWAITING;
}

function CreateInvoiceView({ initialPatient, onClearInitial }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [pendingBillsResults, setPendingBillsResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [showAddOnsSection, setShowAddOnsSection] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [items, setItems] = useState([emptyItem()]);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [roundOff, setRoundOff] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [payments, setPayments] = useState([]);
  const [savedBill, setSavedBill] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [isFinalized, setIsFinalized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [paymentCompleteFlash, setPaymentCompleteFlash] = useState(false);
  const [paymentSuccessModal, setPaymentSuccessModal] = useState(null);
  const [paymentFailureModal, setPaymentFailureModal] = useState(null);
  const [razorpayModalOpen, setRazorpayModalOpen] = useState(false);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [issueDate] = useState(() => new Date().toISOString().slice(0, 10));

  const admissionStatus = selectedPatient ? getAdmissionStatus(selectedPatient.uhid) : null;

  const itemTotals = useMemo(() => {
    return items.map((row) => {
      const net = row.quantity * row.unitPrice - (row.itemDiscount || 0);
      const tax = taxInclusive ? 0 : (net * (row.taxPercent || 0)) / 100;
      const itemTotal = taxInclusive ? net : net + tax;
      return { ...row, net, tax, itemTotal };
    });
  }, [items, taxInclusive]);

  const subtotal = useMemo(() => itemTotals.reduce((s, r) => s + r.itemTotal, 0), [itemTotals]);
  const totalTax = useMemo(() => itemTotals.reduce((s, r) => s + r.tax, 0), [itemTotals]);
  const afterDiscount = Math.max(0, subtotal - (globalDiscount || 0));
  const grandTotalBeforeRound = afterDiscount;
  const grandTotal = Math.round((grandTotalBeforeRound + (roundOff || 0)) * 100) / 100;
  const totalPaid = useMemo(() => payments.reduce((s, p) => s + (p.amount || 0), 0), [payments]);
  const dueAmount = Math.max(0, grandTotal - totalPaid);
  const billStatus = getBillStatus(grandTotal, totalPaid, dueDate, isFinalized);

  // Keep payment amount in sync with due amount so it shows Grand Total / Due by default
  useEffect(() => {
    setPaymentAmount(dueAmount);
  }, [dueAmount]);
  const invoiceNumber = savedBill?.invoice_number || savedBill?.invoiceNumber || generateInvoiceNumber();
  const currentUser = localStorage.getItem("swastik_username") || "billing";
  const simulatedIp = "192.168.1.1";

  const addPayment = useCallback((entry) => {
    const amount = entry.amount ?? paymentAmount;
    const paymentStatus =
      grandTotal > 0 && totalPaid + amount >= grandTotal ? "Completed" : entry.status || "Completed";
    const newPayment = {
      id: `pay-${Date.now()}`,
      date: entry.paymentDate || new Date().toISOString().slice(0, 10),
      method: entry.method || paymentMethod,
      reference: entry.transaction_reference || entry.reference || paymentNotes,
      amount,
      status: paymentStatus,
      createdBy: currentUser,
      timestamp: new Date().toISOString(),
      ip: simulatedIp,
      transactionId: entry.transactionId,
      receiptNumber: entry.receiptNumber,
    };
    setPayments((prev) => [...prev, newPayment]);
    setPaymentAmount(0);
    setPaymentNotes("");
    if (grandTotal > 0 && totalPaid + amount >= grandTotal) setPaymentCompleteFlash(true);
  }, [paymentMethod, paymentAmount, paymentNotes, grandTotal, totalPaid, currentUser]);

  const removePayment = useCallback((id) => {
    setPayments((prev) => prev.filter((p) => p.id !== id));
  }, []);

  useEffect(() => {
    if (!paymentCompleteFlash) return;
    const t = setTimeout(() => setPaymentCompleteFlash(false), 2000);
    return () => clearTimeout(t);
  }, [paymentCompleteFlash]);

  useEffect(() => {
    if (!dirty || !selectedPatient) return;
    const t = setInterval(() => {
      setSavedBill((b) => (b ? { ...b, draft: true, lastAutoSave: new Date().toISOString() } : { draft: true, patientName: selectedPatient.fullName, uhid: selectedPatient.uhid, lastAutoSave: new Date().toISOString() }));
    }, 20000);
    return () => clearInterval(t);
  }, [dirty, selectedPatient]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirty && !isFinalized) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    loadRazorpayScript();
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, isFinalized]);

  useEffect(() => {
    if (initialPatient) {
      handleSelectPatient(initialPatient);
      if (onClearInitial) onClearInitial();
    }
  }, [initialPatient, onClearInitial]);

  const cleanAlphanumeric = (str) => (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");

  const handleSearch = async () => {
    const q = (searchQuery || "").trim();
    if (!q) {
      setPatientSearchResults([]);
      setPendingBillsResults([]);
      setSearchDone(false);
      return;
    }
    setSearching(true);
    setSearchDone(true);
    setPendingBillsResults([]);
    setPatientSearchResults([]);

    try {
      // 1. Search live bills from database (matches invoice_number, uhid, patient_id, or patient_name)
      const bills = await api.searchBills(q, 0, 50).catch(() => []);
      const list = Array.isArray(bills) ? bills : [];
      setPendingBillsResults(list);

      // 2. Fetch live patients from database
      let livePatients = [];
      try {
        const patientsData = await api.getPatients(0, 500);
        if (Array.isArray(patientsData)) {
          livePatients = patientsData;
        }
      } catch (err) {
        console.error("Patient list fetch error:", err);
      }

      // Also extract patient info from any matched bills
      list.forEach(b => {
        if (b.patient_name || b.uhid) {
          livePatients.push({
            _id: b.patient_id || b.uhid,
            uhid: b.uhid || b.patient_id,
            name: b.patient_name,
            fullName: b.patient_name,
            phone: "—"
          });
        }
      });

      // Also attempt direct UHID backend fetch
      try {
        const directPatient = await api.getPatientByUhid(q).catch(() => null);
        if (directPatient && (directPatient.uhid || directPatient.name)) {
          const exists = livePatients.some(p => (p.uhid && p.uhid === directPatient.uhid) || (p._id && p._id === directPatient._id));
          if (!exists) {
            livePatients.unshift(directPatient);
          }
        }
      } catch (e) {}

      // Get local fallback patients
      const localPatients = searchPatients(q);

      // Filter live patients using smart normalized matching (by Name, UHID, Contact, Case No)
      const qLower = q.toLowerCase();
      const qClean = cleanAlphanumeric(q);

      const matchedLive = livePatients.filter((p) => {
        const name = (p.name || p.fullName || "").toLowerCase();
        const uhid = (p.uhid || p.patient_id || p.id || "").toLowerCase();
        const phone = (p.phone || p.contact || p.contact_number || "").replace(/\s/g, "");
        const caseNo = (p.caseNumber || p.case_number || "").toLowerCase();

        if (name.includes(qLower) || uhid.includes(qLower) || phone.includes(qLower) || caseNo.includes(qLower)) {
          return true;
        }
        if (qClean.length >= 1) {
          if (cleanAlphanumeric(uhid).includes(qClean) || cleanAlphanumeric(name).includes(qClean) || cleanAlphanumeric(caseNo).includes(qClean)) {
            return true;
          }
        }
        return false;
      });

      const mappedLive = matchedLive.map((p) => ({
        id: p._id || p.uhid,
        uhid: p.uhid || p.patient_id || p._id,
        fullName: p.name || p.fullName,
        name: p.name || p.fullName,
        phone: p.phone || p.contact || p.contact_number || "—",
        contact: p.phone || p.contact || p.contact_number || "—",
        age: p.age || "—",
        gender: p.gender || "—",
        address: p.address || "—",
      }));

      // Combine backend and local results, avoiding duplicates by UHID
      const mergedMap = new Map();
      mappedLive.forEach((p) => { if (p.uhid) mergedMap.set(p.uhid.toLowerCase(), p); });
      localPatients.forEach((p) => { if (p.uhid && !mergedMap.has(p.uhid.toLowerCase())) mergedMap.set(p.uhid.toLowerCase(), p); });

      const finalPatients = Array.from(mergedMap.values());
      setPatientSearchResults(finalPatients);
    } catch (err) {
      console.error("Search failed:", err);
      setPatientSearchResults(searchPatients(q));
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPendingBill = (bill) => {
    const patient = {
      uhid: bill.uhid || bill.patient_id,
      fullName: bill.patient_name || bill.fullName || "",
      contact: bill.phone || bill.contact || "",
      visitType: "OPD",
    };
    setSelectedPatient(patient);
    setSavedBill(bill);
    setPendingBillsResults([]);
    setPatientSearchResults([]);
    setSearchQuery("");
    setSearchDone(false);
    setShowAddOnsSection(true);

    const billDiscount = Number(bill.discount) || 0;
    setGlobalDiscount(billDiscount);
    setRoundOff(0);

    const billItems = bill.items || [];
    let rows = [];
    if (billItems.length > 0) {
      rows = billItems.map((it, i) => {
        const qty = Number(it.quantity) || 1;
        const price = Number(it.price ?? it.unitPrice ?? it.amount) || 0;
        const taxAmt = Number(it.tax) || 0;
        const net = qty * price;
        const taxPercent = net > 0 ? (100 * taxAmt / net) : (it.taxPercent ?? 0);
        return {
          id: `item-${bill.id || "bill"}-${i}`,
          category: it.category || "Consultation",
          itemName: it.item_name || it.itemName || it.description || "",
          quantity: qty,
          unitPrice: price,
          taxPercent: Math.round(taxPercent * 100) / 100,
          itemDiscount: Number(it.itemDiscount) || 0,
        };
      });
    } else {
      const subtotalVal = Number(bill.subtotal) || Number(bill.total) || 0;
      const taxVal = Number(bill.tax) || 0;
      const taxPct = subtotalVal > 0 ? (100 * taxVal / subtotalVal) : 0;
      rows = [
        {
          ...emptyItem(),
          id: `item-${bill.id || "bill"}-0`,
          itemName: "Loaded Charge",
          category: "Consultation",
          unitPrice: subtotalVal,
          quantity: 1,
          taxPercent: Math.round(taxPct * 100) / 100,
          itemDiscount: 0,
        },
      ];
    }
    setItems(rows);

    const existingPayments = (bill.payments || []).map((p, i) => ({
      id: `pay-${bill.id}-${i}`,
      date: (p.payment_date || "").slice(0, 10),
      method: p.method || "Cash",
      reference: p.transaction_reference || "",
      amount: Number(p.amount) || 0,
      status: "Completed",
      createdBy: p.created_by || currentUser,
      timestamp: p.payment_date || "",
    }));
    setPayments(existingPayments);

    const totalPaidCalc = existingPayments.reduce((s, p) => s + p.amount, 0);
    const calculatedTotal = Number(bill.total) || rows.reduce((s, r) => s + (r.quantity * r.unitPrice), 0);
    const dueVal = bill.due_amount !== undefined ? Number(bill.due_amount) : Math.max(0, calculatedTotal - totalPaidCalc);
    setPaymentAmount(dueVal);
    setDirty(true);
  };

  const handleSelectPatient = async (p) => {
    setSelectedPatient(p);
    setPatientSearchResults([]);
    setSearchQuery("");
    setSearchDone(false);

    // Auto-search for pending bills for this patient
    setSearching(true);
    try {
      const bills = await api.searchBills(p.uhid, 0, 50);
      setPendingBillsResults(Array.isArray(bills) ? bills : []);
    } catch {
      setPendingBillsResults([]);
    } finally {
      setSearching(false);
    }

    setShowAddOnsSection(true);
    const visitType = (p.visitType || "opd").toLowerCase();
    const defaults = getDefaultItemsForVisitType(visitType);
    setItems(defaults.length ? defaults.map((d, i) => ({ ...emptyItem(), ...d, taxPercent: 5, id: `item-${Date.now()}-${i}` })) : [emptyItem()]);
    setSavedBill(null);
    setPayments([]);
    setDirty(true);
  };

  const refreshLoadedBill = useCallback(async () => {
    if (!savedBill?.id) return;
    try {
      const updated = await api.getBill(savedBill.id);
      if (!updated) return;
      setSavedBill(updated);
      const existingPayments = (updated.payments || []).map((p, i) => ({
        id: `pay-${updated.id}-${i}`,
        date: (p.payment_date || "").slice(0, 10),
        method: p.method || "Cash",
        reference: p.transaction_reference || "",
        amount: Number(p.amount) || 0,
        status: "Completed",
        createdBy: p.created_by || currentUser,
        timestamp: p.payment_date || "",
      }));
      setPayments(existingPayments);
      setPaymentAmount(Number(updated.due_amount) || 0);
    } catch (e) {
      console.error("Refresh bill failed", e);
    }
  }, [savedBill?.id, currentUser]);

  const addRow = () => { setItems((prev) => [...prev, emptyItem()]); setDirty(true); };
  const removeRow = (id) => { setItems((prev) => prev.filter((r) => r.id !== id)); setDirty(true); };
  const updateItem = (id, field, value) => {
    setDirty(true);
    setItems((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, [field]: ["quantity", "unitPrice", "taxPercent", "itemDiscount"].includes(field) ? (typeof value === "string" ? parseFloat(value) || 0 : value) : value }
          : r
      )
    );
  };

  const saveOrUpdateBill = useCallback(async (statusOverride = null) => {
    if (!selectedPatient) return null;
    const itemsForApi = itemTotals.map((r) => ({
      category: r.category,
      item_name: r.itemName,
      quantity: r.quantity,
      price: r.unitPrice,
      tax: r.tax,
      total: r.itemTotal,
    }));
    const payload = {
      patient_id: selectedPatient.uhid,
      uhid: selectedPatient.uhid,
      patient_name: selectedPatient.fullName || selectedPatient.name || "",
      subtotal,
      tax: totalTax,
      discount: globalDiscount || 0,
      total: grandTotal,
      items: itemsForApi,
      created_by: currentUser,
    };
    if (statusOverride) {
      payload.status = statusOverride;
    }

    const billId = savedBill?.id || savedBill?._id;
    if (billId) {
      const updated = await api.updateBill(billId, payload).catch(() => null);
      if (updated) {
        setSavedBill({ ...updated, patientName: selectedPatient.fullName });
        return updated.id || updated._id;
      }
    }

    const created = await api.createBill(payload);
    setSavedBill({ ...created, patientName: selectedPatient.fullName });
    return created.id || created._id;
  }, [savedBill, selectedPatient, itemTotals, subtotal, totalTax, globalDiscount, grandTotal, currentUser]);

  const handleSaveDraft = async () => {
    if (!selectedPatient) return;
    setApiError(null);
    try {
      const billId = await saveOrUpdateBill("Draft");
      if (billId) {
        setDirty(false);
      }
    } catch (err) {
      setApiError(err?.message || "Failed to save draft");
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedPatient) {
      setApiError("Please select a patient first.");
      return;
    }
    setApiError(null);
    try {
      const billId = await saveOrUpdateBill();
      if (!billId) throw new Error("Failed to save bill");

      const amount = Number(paymentAmount) || 0;
      let resBill = null;
      if (amount > 0) {
        const res = await api.addBillPayment(billId, {
          amount,
          method: paymentMethod,
          transaction_reference: paymentNotes || "",
          created_by: currentUser,
        });
        if (res && res.bill) {
          resBill = res.bill;
        }
      }

      if (!resBill) {
        resBill = await api.getBill(billId);
      }

      if (resBill) {
        setSavedBill(resBill);
        const existingPayments = (resBill.payments || []).map((p, i) => ({
          id: `pay-${resBill.id}-${i}`,
          date: (p.payment_date || "").slice(0, 10),
          method: p.method || "Cash",
          reference: p.transaction_reference || "",
          amount: Number(p.amount) || 0,
          status: "Completed",
          createdBy: p.created_by || currentUser,
          timestamp: p.payment_date || "",
        }));
        setPayments(existingPayments);
        setPaymentAmount(Number(resBill.due_amount) || 0);
      }

      setIsFinalized(true);
      setDirty(false);
      setPaymentCompleteFlash(true);
    } catch (err) {
      setApiError(err?.message || "Failed to record payment");
    }
  };

  const handleRazorpayClick = async () => {
    if (isFinalized) return;
    setApiError(null);
    let bId = savedBill?.id || savedBill?._id;
    if (!bId) {
      if (!selectedPatient) {
        setApiError("Please select a patient first.");
        return;
      }
      try {
        bId = await saveOrUpdateBill();
        if (!bId) throw new Error("Could not create bill");
      } catch (err) {
        setApiError(err.message || "Please save the bill first.");
        return;
      }
    }
    setRazorpayModalOpen(true);
  };

  const handleAddNonUpiPayment = async () => {
    const amount = Number(paymentAmount) || 0;
    if (amount <= 0) return;
    if (paymentMethod === "Online (Razorpay)") {
      handleRazorpayClick();
      return;
    }
    if (!selectedPatient) {
      setApiError("Please select a patient first.");
      return;
    }

    try {
      setApiError(null);
      const billId = await saveOrUpdateBill();
      if (!billId) throw new Error("Failed to save bill");

      const res = await api.addBillPayment(billId, {
        amount,
        method: paymentMethod,
        transaction_reference: paymentNotes || "",
        created_by: currentUser,
      });

      if (res && res.bill) {
        setSavedBill(res.bill);
        const existingPayments = (res.bill.payments || []).map((p, i) => ({
          id: `pay-${res.bill.id}-${i}`,
          date: (p.payment_date || "").slice(0, 10),
          method: p.method || "Cash",
          reference: p.transaction_reference || "",
          amount: Number(p.amount) || 0,
          status: "Completed",
          createdBy: p.created_by || currentUser,
          timestamp: p.payment_date || "",
        }));
        setPayments(existingPayments);
        const dueVal = Number(res.bill.due_amount) || 0;
        setPaymentAmount(dueVal);
        if (dueVal <= 0) {
          setIsFinalized(true);
        }
      } else {
        await refreshLoadedBill();
      }

      setPaymentNotes("");
      setDirty(false);
      setPaymentCompleteFlash(true);

      if (res && res.receipt_number) {
        setPaymentSuccessModal({
          receipt_number: res.receipt_number,
          payment_date: res.payment_date,
          amount: res.amount,
          method: res.method,
          transaction_reference: res.transaction_reference || paymentNotes,
          patientName: selectedPatient?.fullName,
          uhid: selectedPatient?.uhid,
          invoice_number: res.bill?.invoice_number || savedBill?.invoice_number,
        });
      }
    } catch (err) {
      setApiError(err?.message || "Failed to add payment");
    }
  };

  const handleRazorpayVerifySuccess = (res) => {
    if (res && res.success) {
      setPaymentSuccessModal({
        receipt_number: res.receipt_number,
        payment_date: res.bill?.payments?.[res.bill.payments.length - 1]?.payment_date || new Date().toISOString(),
        amount: res.bill?.payments?.[res.bill.payments.length - 1]?.amount || 0,
        method: "Razorpay",
        transaction_reference: res.bill?.payments?.[res.bill.payments.length - 1]?.transaction_reference || "",
        patientName: selectedPatient?.fullName,
        uhid: selectedPatient?.uhid,
        invoice_number: res.bill?.invoice_number || savedBill?.invoice_number,
      });
      setPayments(res.bill?.payments || []);
      setSavedBill(res.bill);
      setPaymentAmount(Number(res.bill?.due_amount) || 0);
      setDirty(false);
    }
  };

  const handleRazorpayModalSuccess = useCallback(
    async (amount, options = {}) => {
      const method = options.method || "Online (Razorpay)";
      const transactionReference = (options.transactionReference || "").trim();
      const bId = savedBill?.id || savedBill?._id;
      if (bId) {
        try {
          setApiError(null);
          const res = await api.addBillPayment(bId, {
            amount: Number(amount) || 0,
            method,
            transaction_reference: transactionReference,
          });
          if (res && res.success) {
            handleRazorpayVerifySuccess(res);
            return { success: true, receipt_number: res.receipt_number };
          }
          return { success: false };
        } catch (err) {
          setApiError(err?.message || "Failed to add payment");
          return { success: false };
        }
      }
      return { success: false };
    },
    [savedBill, selectedPatient]
  );

  const handleUpiPaymentConfirmed = async (entry) => {
    if (!selectedPatient) return;
    try {
      const billId = await saveOrUpdateBill();
      if (billId) {
        const res = await api.addBillPayment(billId, {
          amount: entry.amount,
          method: "UPI",
          transaction_reference: entry.transaction_reference || entry.reference || "",
          created_by: currentUser,
        });
        if (res && res.bill) {
          setSavedBill(res.bill);
          setPayments(res.bill.payments || []);
          setPaymentAmount(Number(res.bill.due_amount) || 0);
          if ((Number(res.bill.due_amount) || 0) <= 0) setIsFinalized(true);
        }
      }
    } catch (err) {
      console.error("UPI payment error:", err);
    }
  };

  const ensureBillCreated = useCallback(async () => {
    return await saveOrUpdateBill();
  }, [saveOrUpdateBill]);


  const getPrintLogoUrl = () => {
    return hospitalLogo;
  };

  const printStyles = `
    @page { size: A4; margin: 15mm; }
    body { font-family: 'Segoe UI', Inter, system-ui, sans-serif; padding: 0; margin: 0; background: #ffffff; color: #1e293b; }
    .invoice-print-body { width: 100%; max-width: 800px; margin: 0 auto; padding: 20px; box-sizing: border-box; }
    @media print {
      body { background: white !important; }
      .no-print { display: none !important; }
    }
  `;

  const buildInvoiceHtml = (logoUrl) => {
    const totalPaidCalc = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const due = Math.max(0, grandTotal - totalPaidCalc);
    const rows = itemTotals.map((r) => `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 10px 12px; font-weight: 500; color: #0f172a;">${r.itemName || "—"}</td>
        <td style="padding: 10px 12px; color: #64748b;">${r.category || "Consultation"}</td>
        <td style="padding: 10px 12px; text-align: center; color: #334155;">${r.quantity}</td>
        <td style="padding: 10px 12px; text-align: right; color: #334155;">₹${(r.unitPrice ?? 0).toFixed(2)}</td>
        <td style="padding: 10px 12px; text-align: right; color: #64748b;">${r.taxPercent ?? 0}%</td>
        <td style="padding: 10px 12px; text-align: right; font-weight: 600; color: #0f766e;">₹${(r.itemTotal ?? 0).toFixed(2)}</td>
      </tr>
    `).join("");

    const header = logoUrl
      ? `<div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #0f766e; padding-bottom: 16px; margin-bottom: 20px;">
          <div style="display: flex; align-items: center; gap: 16px;">
            <img src="${logoUrl}" alt="Swastik Hospital Logo" style="height: 55px; width: auto; max-width: 160px; object-fit: contain; display: block;" />
            <div>
              <h1 style="margin: 0; font-size: 1.35rem; color: #0f766e; font-weight: 700; letter-spacing: -0.02em;">Swastik Hospital</h1>
              <p style="margin: 3px 0 0; font-size: 0.85rem; color: #64748b; font-weight: 500;">Multi-Specialty & Mental Health Care Center</p>
              <p style="margin: 2px 0 0; font-size: 0.78rem; color: #94a3b8;">GSTIN: 27AABCU9603R1ZM</p>
            </div>
          </div>
          <div style="text-align: right;">
            <div style="display: inline-block; padding: 4px 12px; background: #ccfbf1; color: #0f766e; border-radius: 6px; font-weight: 700; font-size: 0.82rem; letter-spacing: 0.05em; text-transform: uppercase;">TAX INVOICE</div>
            <p style="margin: 6px 0 0; font-size: 0.95rem; font-weight: 700; color: #0f172a;"># ${invoiceNumber}</p>
            <p style="margin: 2px 0 0; font-size: 0.8rem; color: #64748b;">Date: ${issueDate}</p>
          </div>
        </div>`
      : `<div style="border-bottom: 2px solid #0f766e; padding-bottom: 16px; margin-bottom: 20px;">
          <h1 style="margin: 0; font-size: 1.35rem; color: #0f766e; font-weight: 700;">Swastik Hospital</h1>
          <p style="margin: 4px 0 0; font-size: 0.85rem; color: #64748b;">GSTIN: 27AABCU9603R1ZM</p>
        </div>`;

    return `
      <div class="invoice-print-body">
        ${header}

        <div style="display: flex; justify-content: space-between; gap: 16px; background: #f8fafc; padding: 16px 20px; border-radius: 12px; margin-bottom: 20px; border: 1px solid #e2e8f0;">
          <div>
            <p style="margin: 0 0 4px; font-size: 0.75rem; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: 0.05em;">Patient Info</p>
            <p style="margin: 0; font-size: 1.05rem; font-weight: 700; color: #0f172a;">${selectedPatient?.fullName || selectedPatient?.name || "Walk-in Patient"}</p>
            <p style="margin: 3px 0 0; font-size: 0.85rem; color: #475569;">UHID: <strong>${selectedPatient?.uhid || "N/A"}</strong> ${selectedPatient?.contact ? `| Contact: ${selectedPatient.contact}` : ""}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0 0 4px; font-size: 0.75rem; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: 0.05em;">Invoice Status</p>
            <p style="margin: 0; font-size: 0.9rem; color: #475569;">Status: <span style="font-weight: 700; color: ${due <= 0 ? '#059669' : '#d97706'}">${billStatus}</span></p>
            <p style="margin: 3px 0 0; font-size: 0.85rem; color: #475569;">Due Date: <strong>${dueDate}</strong></p>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 0.88rem;">
          <thead>
            <tr style="background: #0f766e; color: #ffffff;">
              <th style="padding: 10px 12px; text-align: left; border-top-left-radius: 8px;">Particulars</th>
              <th style="padding: 10px 12px; text-align: left;">Category</th>
              <th style="padding: 10px 12px; text-align: center;">Qty</th>
              <th style="padding: 10px 12px; text-align: right;">Rate (₹)</th>
              <th style="padding: 10px 12px; text-align: right;">Tax %</th>
              <th style="padding: 10px 12px; text-align: right; border-top-right-radius: 8px;">Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${rows || '<tr><td colspan="6" style="text-align:center; padding: 20px; color: #94a3b8;">No line items</td></tr>'}
          </tbody>
        </table>

        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-top: 20px;">
          <div style="flex: 1; font-size: 0.8rem; color: #64748b; background: #f8fafc; padding: 14px 16px; border-radius: 10px; border: 1px solid #f1f5f9;">
            <p style="margin: 0 0 6px; font-weight: 700; color: #334155; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em;">Terms & Conditions</p>
            <p style="margin: 0 0 4px;">• All psychiatric & medical records are kept strictly confidential.</p>
            <p style="margin: 0 0 4px;">• Payments once made are non-refundable.</p>
            <p style="margin: 0;">• This is a computer-generated tax invoice.</p>
          </div>

          <div style="width: 260px; font-size: 0.88rem;">
            <div style="display: flex; justify-content: space-between; padding: 4px 0; color: #475569;">
              <span>Subtotal:</span>
              <span>₹${subtotal.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0; color: #475569;">
              <span>Tax (GST):</span>
              <span>₹${totalTax.toFixed(2)}</span>
            </div>
            ${(globalDiscount > 0) ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0; color: #059669; font-weight: 600;">
              <span>Discount:</span>
              <span>- ₹${globalDiscount.toFixed(2)}</span>
            </div>` : ""}
            ${(roundOff !== 0) ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0; color: #64748b;">
              <span>Round off:</span>
              <span>₹${roundOff.toFixed(2)}</span>
            </div>` : ""}
            <div style="display: flex; justify-content: space-between; padding: 8px 0; margin-top: 6px; border-top: 2px solid #0f766e; border-bottom: 2px solid #0f766e; font-size: 1.05rem; font-weight: 700; color: #0f766e;">
              <span>Grand Total:</span>
              <span>₹${grandTotal.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0; margin-top: 6px; color: #059669; font-weight: 600;">
              <span>Paid Amount:</span>
              <span>₹${totalPaidCalc.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0; color: ${due > 0 ? '#b91c1c' : '#475569'}; font-weight: 700;">
              <span>Balance Due:</span>
              <span>₹${due.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div style="margin-top: 40px; padding-top: 16px; border-top: 1px dashed #cbd5e1; display: flex; justify-content: space-between; align-items: flex-end;">
          <div>
            <p style="margin: 0; font-size: 0.78rem; color: #94a3b8;">Generated on: ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
          </div>
          <div style="text-align: center;">
            <div style="height: 35px;"></div>
            <p style="margin: 0; border-top: 1px solid #94a3b8; width: 180px; padding-top: 4px; font-size: 0.8rem; color: #475569; font-weight: 600;">Authorized Signatory</p>
          </div>
        </div>
      </div>
    `;
  };

  const buildPaymentReceiptHtml = (receipt, logoUrl) => {
    if (!receipt || !receipt.receipt_number) return "";
    const dateStr = receipt.payment_date ? new Date(receipt.payment_date).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    const header = logoUrl
      ? `<div style="display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #0f766e; padding-bottom: 12px; margin-bottom: 16px;">
          <img src="${logoUrl}" alt="Swastik Hospital Logo" style="height: 50px; width: auto; max-width: 140px; object-fit: contain; display: block;" />
          <div>
            <h1 style="margin: 0; font-size: 1.25rem; color: #0f766e; font-weight: 700;">Swastik Hospital</h1>
            <p style="margin: 2px 0 0; font-size: 0.8rem; color: #64748b;">GSTIN: 27AABCU9603R1ZM</p>
          </div>
        </div>`
      : `<div style="border-bottom: 2px solid #0f766e; padding-bottom: 12px; margin-bottom: 16px;">
          <h1 style="margin: 0; font-size: 1.25rem; color: #0f766e; font-weight: 700;">Swastik Hospital</h1>
          <p style="margin: 2px 0 0; font-size: 0.8rem; color: #64748b;">GSTIN: 27AABCU9603R1ZM</p>
        </div>`;
    return `
<div class="receipt-print" style="font-family: 'Segoe UI', Inter, system-ui, sans-serif; max-width: 480px; margin: 0 auto; padding: 20px; background: #fff;">
  ${header}
  <h2 style="text-align: center; font-size: 1.15rem; margin: 16px 0; color: #0f766e; font-weight: 700; letter-spacing: 0.05em;">PAYMENT RECEIPT</h2>
  <table style="width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 0.9rem;">
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">Receipt No.</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 700; color: #0f172a;">${receipt.receipt_number}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">Date & Time</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #334155;">${dateStr}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">Patient Name</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #0f172a;">${receipt.patientName || "—"}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">UHID</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #334155;">${receipt.uhid || "—"}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">Invoice No.</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #334155;">${receipt.invoice_number || "—"}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">Amount Paid</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 700; color: #059669; font-size: 1.05rem;">₹${Number(receipt.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">Payment Method</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #334155;">${receipt.method || "—"}</td></tr>
    <tr><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #64748b; font-weight: 500;">UTR / Ref ID</td><td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #334155;">${receipt.transaction_reference || "—"}</td></tr>
  </table>
  <p style="font-size: 0.8rem; color: #64748b; margin-top: 20px; text-align: center;">This is a computer-generated receipt and does not require a signature.</p>
  <p style="font-size: 0.78rem; color: #94a3b8; text-align: center; margin-top: 4px;">All psychiatric & medical records are confidential.</p>
</div>`;
  };

  const handlePrintReceipt = () => {
    if (!paymentSuccessModal || typeof paymentSuccessModal !== "object") return;
    const logoUrl = getPrintLogoUrl();
    const html = buildPaymentReceiptHtml(paymentSuccessModal, logoUrl);
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Payment Receipt - ${paymentSuccessModal.receipt_number}</title><style>${printStyles} .receipt-print{max-width:480px;margin:0 auto}.receipt-title{text-align:center;font-size:1.25rem;margin:16px 0;color:#0f766e}.receipt-table{width:100%;border-collapse:collapse;margin:16px 0}.receipt-table td{padding:8px 12px;border-bottom:1px solid #e2e8f0}.receipt-table td:first-child{width:45%;color:#64748b}.receipt-footer,.receipt-confidential{font-size:0.85rem;color:#64748b;margin-top:20px}</style></head><body>${html}</body></html>`);
    w.document.close();
    w.print();
    w.close();
  };

  const handlePrint = () => {
    const logoUrl = getPrintLogoUrl();
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Invoice</title><style>${printStyles}</style></head><body>${buildInvoiceHtml(logoUrl)}</body></html>`);
    w.document.close();
    w.print();
    w.close();
  };

  const statusClass = {
    [BILL_STATUS.DRAFT]: "create-invoice-status--draft",
    [BILL_STATUS.AWAITING]: "create-invoice-status--awaiting",
    [BILL_STATUS.PARTIAL]: "create-invoice-status--partial",
    [BILL_STATUS.PAID]: "create-invoice-status--paid",
    [BILL_STATUS.OVERDUE]: "create-invoice-status--overdue",
  };

  return (
    <div className="billing-content create-invoice-page">
      {paymentCompleteFlash && <div className="create-invoice-flash" aria-live="polite">Payment completed</div>}

      <div className="billing-card create-invoice-card">
        <div className="create-invoice-status-row">
          <span className="create-invoice-status-label">Invoice status</span>
          <span className={`create-invoice-status-badge ${statusClass[billStatus] || ""}`}>{billStatus}</span>
        </div>

        <h2 className="billing-card__heading">Create Invoice</h2>
        <div className="billing-form-row">
          <label>Patient</label>
          <div className="billing-search-row">
            <input type="text" placeholder="Search by patient name, UHID, or Invoice #" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} disabled={isFinalized} />
            <button type="button" className="billing-btn billing-btn--primary" onClick={handleSearch} disabled={isFinalized || !searchQuery.trim()}>
              {searching ? "Searching…" : "Search"}
            </button>
          </div>
          {pendingBillsResults.length > 0 && (
            <div className="billing-card create-invoice-pending-list">
              <h4 className="create-invoice-pending-title">Pending bills found (click to load)</h4>
              <div className="billing-table-wrap">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Patient</th>
                      <th>UHID</th>
                      <th>Pending (₹)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingBillsResults.map((b) => (
                      <tr key={b.id || b._id} className="create-invoice-pending-row" onClick={() => handleSelectPendingBill(b)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleSelectPendingBill(b)}>
                        <td>{b.invoice_number}</td>
                        <td>{b.patient_name}</td>
                        <td><strong>{b.uhid || b.patient_id || "—"}</strong></td>
                        <td><strong>₹{Number(b.due_amount || 0).toLocaleString()}</strong></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="billing-btn billing-btn--sm billing-btn--primary" onClick={() => handleSelectPendingBill(b)}>Load Bill</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!searching && patientSearchResults.length > 0 && (
            <div className="billing-dropdown">
              <p className="create-invoice-search-hint">Select patient to create new invoice:</p>
              {patientSearchResults.map((p) => (
                <button type="button" key={p.uhid} className="billing-dropdown-item" onClick={() => handleSelectPatient(p)}>
                  <strong>{p.uhid}</strong> – {p.fullName} {p.contact && p.contact !== "—" ? `(${p.contact})` : ""}
                </button>
              ))}
            </div>
          )}
          {!searching && searchDone && searchQuery.trim() && !selectedPatient && pendingBillsResults.length === 0 && patientSearchResults.length === 0 && (
            <div className="create-invoice-no-results" style={{ marginTop: "12px", padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#991b1b", fontSize: "0.9rem" }}>
              No patient or bill found matching "<strong>{searchQuery}</strong>". Please verify the Patient Name or UHID and try again.
            </div>
          )}
          {selectedPatient && (
            <div className="billing-patient-info">
              <p><strong>UHID:</strong> {selectedPatient.uhid} | <strong>Name:</strong> {selectedPatient.fullName}</p>
              <p><strong>Visit:</strong> {selectedPatient.visitType || "OPD"} {selectedPatient.riskFlag && "| High Risk"} {admissionStatus && `| Admission: ${admissionStatus.status}`}</p>
              <p><strong>Issue date:</strong> {issueDate} | <strong>Due date:</strong> <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="create-invoice-date" disabled={isFinalized} /></p>
            </div>
          )}
        </div>

        <div className="billing-form-row">
          <label>Items</label>
          {savedBill?.id && !showAddOnsSection ? (
            <div className="create-invoice-addons-collapsed">
              <p className="create-invoice-addons-summary">
                Loaded bill: <strong>{savedBill.invoice_number}</strong> — Grand Total ₹{grandTotal.toFixed(2)}, Due ₹{dueAmount.toFixed(2)} (includes 5% tax).
              </p>
              <p className="create-invoice-addons-hint">Add extra charges (consultation, procedures, etc.) to this bill.</p>
              <button type="button" className="billing-btn billing-btn--primary create-invoice-addons-btn" onClick={() => setShowAddOnsSection(true)} disabled={isFinalized}>
                + Add ons (additional charges)
              </button>
            </div>
          ) : (
            <>
              {savedBill?.id && showAddOnsSection && (
                <div className="create-invoice-addons-toggle">
                  <button type="button" className="billing-btn billing-btn--secondary billing-btn--sm" onClick={() => setShowAddOnsSection(false)}>Hide add-ons section</button>
                </div>
              )}
              <div className="billing-table-wrap">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Category</th>
                      <th>Qty</th>
                      <th>Unit Price (₹)</th>
                      <th>Tax %</th>
                      <th>Item Disc. (₹)</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemTotals.map((row) => (
                      <tr key={row.id}>
                        <td><input type="text" value={row.itemName} onChange={(e) => updateItem(row.id, "itemName", e.target.value)} placeholder="Item" disabled={isFinalized} /></td>
                        <td>
                          <select value={row.category} onChange={(e) => updateItem(row.id, "category", e.target.value)} disabled={isFinalized}>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td><input type="number" min={1} value={row.quantity} onChange={(e) => updateItem(row.id, "quantity", e.target.value)} disabled={isFinalized} /></td>
                        <td><input type="number" min={0} step={0.01} value={row.unitPrice} onChange={(e) => updateItem(row.id, "unitPrice", e.target.value)} disabled={isFinalized} /></td>
                        <td><input type="number" min={0} max={100} value={row.taxPercent} onChange={(e) => updateItem(row.id, "taxPercent", e.target.value)} disabled={isFinalized} /></td>
                        <td><input type="number" min={0} value={row.itemDiscount || 0} onChange={(e) => updateItem(row.id, "itemDiscount", e.target.value)} disabled={isFinalized} /></td>
                        <td className="billing-table__total">{row.itemTotal.toFixed(2)}</td>
                        <td>{!isFinalized && <button type="button" className="billing-btn billing-btn--danger billing-btn--sm" onClick={() => removeRow(row.id)}>Remove</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!isFinalized && <button type="button" className="billing-btn billing-btn--secondary" onClick={addRow}>+ Add Item</button>}
            </>
          )}
        </div>

        <div className="billing-summary-inline create-invoice-totals">
          <div className="billing-summary-row"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
          <div className="billing-summary-row"><span>Tax</span><span>₹{totalTax.toFixed(2)}</span></div>
          <div className="billing-summary-row">
            <span>Tax inclusive</span>
            <input type="checkbox" checked={taxInclusive} onChange={(e) => { setTaxInclusive(e.target.checked); setDirty(true); }} disabled={isFinalized} />
          </div>
          <div className="billing-summary-row">
            <span>Global discount (₹)</span>
            <input type="number" min={0} value={globalDiscount} onChange={(e) => { setGlobalDiscount(parseFloat(e.target.value) || 0); setDirty(true); }} style={{ width: "100px" }} disabled={isFinalized} />
          </div>
          <div className="billing-summary-row">
            <span>Round off (₹)</span>
            <input type="number" step={0.01} value={roundOff} onChange={(e) => { setRoundOff(parseFloat(e.target.value) || 0); setDirty(true); }} style={{ width: "100px" }} disabled={isFinalized} />
          </div>
          <div className="billing-summary-row billing-summary-row--grand"><span>Grand Total</span><span>₹{grandTotal.toFixed(2)}</span></div>
          <div className="billing-summary-row"><span>Total Paid</span><span>₹{totalPaid.toFixed(2)}</span></div>
          <div className="billing-summary-row"><span>Due</span><span>₹{dueAmount.toFixed(2)}</span></div>
        </div>

        <div className="create-invoice-payment-section">
          <h3 className="create-invoice-payment-title">Payment</h3>
          <div className="create-invoice-payment-card">
            {paymentMethod === "UPI" ? (
              <UPIPaymentPanel
                amount={dueAmount > 0 ? dueAmount : grandTotal}
                invoiceNumber={invoiceNumber}
                onPaymentConfirmed={handleUpiPaymentConfirmed}
                disabled={isFinalized}
              />
            ) : (
              <>
                <div className="billing-form-row">
                  <label>Method</label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} disabled={isFinalized}>
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="billing-form-row">
                  <label>{paymentMethod === "Insurance" ? "Insurance amount (₹)" : "Amount (₹)"}</label>
                  <input type="number" min={0} step={0.01} value={paymentAmount} onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)} disabled={isFinalized} />
                </div>
                <div className="billing-form-row">
                  <label>Reference / Notes</label>
                  <input type="text" value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} placeholder="UTR / reference" disabled={isFinalized} />
                </div>
                <button type="button" className="billing-btn billing-btn--secondary" onClick={handleAddNonUpiPayment} disabled={isFinalized}>Add Payment</button>
              </>
            )}
            <RazorpayModal
              open={razorpayModalOpen}
              onClose={() => setRazorpayModalOpen(false)}
              billAmount={paymentAmount > 0 ? paymentAmount : dueAmount}
              patientName={selectedPatient?.fullName}
              billId={savedBill?.id || savedBill?._id}
              onPaymentSuccess={handleRazorpayModalSuccess}
              onVerifySuccess={handleRazorpayVerifySuccess}
              disabled={isFinalized}
            />
          </div>

          {payments.length > 0 && (
            <div className="create-invoice-payment-history">
              <h4>Payment history</h4>
              <div className="billing-table-wrap">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Transaction ID</th>
                      <th>Status</th>
                      <th>User</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>{p.date}</td>
                        <td>{p.method}</td>
                        <td>₹{(p.amount || 0).toFixed(2)}</td>
                        <td>{p.transactionId || p.reference || "—"}</td>
                        <td>{p.status}</td>
                        <td><span className="create-invoice-audit">{p.createdBy}</span> {p.timestamp && <span className="create-invoice-audit-time" title={p.timestamp}>•</span>}</td>
                        <td>{!isFinalized && <button type="button" className="billing-btn billing-btn--danger billing-btn--sm" onClick={() => removePayment(p.id)}>Remove</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {apiError && <p className="billing-payment__error">{apiError}</p>}
        <div className="billing-create-actions no-print">
          {!isFinalized && <button type="button" className="billing-btn billing-btn--secondary" onClick={handleSaveDraft}>Save Draft</button>}
          <button type="button" className="billing-btn billing-btn--secondary" onClick={() => setShowReceiptPreview(!showReceiptPreview)}>Receipt preview</button>
          <button type="button" className="billing-btn billing-btn--secondary" onClick={handlePrint}>Print</button>
          {!isFinalized && (
            <button
              type="button"
              className="billing-btn billing-btn--primary"
              onClick={handleMarkPaid}
              disabled={totalPaid >= grandTotal && grandTotal > 0}
            >
              Finalize & Mark Paid
            </button>
          )}
        </div>
        {showReceiptPreview && (
          <div className="create-invoice-receipt-preview" dangerouslySetInnerHTML={{ __html: buildInvoiceHtml(getPrintLogoUrl()) }} />
        )}
        {savedBill && <p className="billing-card__muted">Bill saved. {savedBill.invoice_number || savedBill.invoiceNumber || "Draft saved."} {savedBill.lastAutoSave && "(Auto-saved)"}</p>}
        {isFinalized && <p className="create-invoice-locked">Invoice locked.</p>}

        {paymentSuccessModal && (
          <div className="billing-modal-overlay" role="dialog" aria-modal="true">
            <div className="billing-modal billing-modal--success">
              <h3>Payment successful</h3>
              {typeof paymentSuccessModal === "object" ? (
                <>
                  <p>Receipt number: <strong>{paymentSuccessModal.receipt_number}</strong></p>
                  <p style={{ fontSize: "0.9rem", color: "#475569", marginTop: "8px" }}>
                    ₹{Number(paymentSuccessModal.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} via {paymentSuccessModal.method || "—"}
                    {paymentSuccessModal.transaction_reference && ` · UTR: ${paymentSuccessModal.transaction_reference}`}
                  </p>
                  <div style={{ display: "flex", gap: "8px", marginTop: "16px", flexWrap: "wrap" }}>
                    <button type="button" className="billing-btn billing-btn--primary" onClick={handlePrintReceipt}>Print receipt</button>
                    <button type="button" className="billing-btn billing-btn--secondary" onClick={() => setPaymentSuccessModal(null)}>Close</button>
                  </div>
                </>
              ) : (
                <>
                  <p>Receipt: <strong>{paymentSuccessModal}</strong></p>
                  <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                    <button type="button" className="billing-btn billing-btn--secondary" onClick={() => setPaymentSuccessModal(null)}>Close</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {paymentFailureModal && (
          <div className="billing-modal-overlay" role="dialog" aria-modal="true">
            <div className="billing-modal billing-modal--failure">
              <h3>Payment failed</h3>
              <p>{paymentFailureModal}</p>
              <button type="button" className="billing-btn billing-btn--primary" onClick={() => setPaymentFailureModal(null)}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateInvoiceView;
